import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

import { useListCustomersAutocompleteQuery } from '@/common/infrastructure/gql/generated/schema';
import { useState, useEffect, useMemo, useCallback } from 'react';
import debounce from 'lodash-es/debounce';

type Customer = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useListCustomersAutocompleteQuery>['data']>['customers']>['items']
>[number];

interface UseListCustomersAutocompleteParams {
  searchTerm?: string;
  debounceMs?: number;
  onCustomerSelect?: (customer: AutocompleteOption) => void;
  skipQuery?: boolean;
  queryOnEmpty?: boolean;
}

export const useListCustomersAutocomplete = ({
  searchTerm = '',
  debounceMs = 1000,
  onCustomerSelect,
  skipQuery = false,
  queryOnEmpty = false
}: UseListCustomersAutocompleteParams = {}) => {
  // Debounce the search term to prevent excessive queries
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);

  const debouncedSetSearch = useMemo(() => debounce((value: string) => setDebouncedSearchTerm(value), debounceMs), [debounceMs]);

  useEffect(() => {
    debouncedSetSearch(searchTerm);
    return () => debouncedSetSearch.cancel();
  }, [searchTerm, debouncedSetSearch]);

  const result = useListCustomersAutocompleteQuery({
    variables: {
      skip: 0,
      where: {
        or: [{ companyName: { contains: debouncedSearchTerm } }, { id: { eq: Number(debouncedSearchTerm) } }]
      }
    },
    fetchPolicy: 'cache-first',
    skip: skipQuery || (!queryOnEmpty && debouncedSearchTerm.length === 0 && searchTerm.length === 0)
  });

  const queryData = useMemo(() => ({ items: result.data?.customers?.items }), [result.data]);

  const transform = useCallback(
    (customer: Customer): AutocompleteOption => ({
      value: customer.id ?? -1,
      label: customer.companyName ?? 'Unknown Customer',
      email: customer.primaryEmail,
      phone: customer.primaryPhone
    }),
    []
  );

  const autocompleteResult = useAutocomplete<Customer>({
    searchTerm,
    debounceMs,
    onSelect: onCustomerSelect,
    query: () => ({
      data: queryData,
      loading: result.loading,
      error: result.error
    }),
    transform
  });

  const rawCustomerData = useMemo(() => (queryData.items ?? []) as Customer[], [queryData]);

  return {
    ...autocompleteResult,
    rawCustomerData
  };
};
